import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { GsdReadableBlock, GsdStandaloneModuleDashboard, useI18n } from "@/coquille-bridge";
import { useDataStore } from "@/components/admin/useDataStore.js";
import {
  DOCUMENTS_PREUVES_MODULE_EN,
  DOCUMENTS_PREUVES_MODULE_FR,
  resolveDocumentsPreuvesBase,
  SHOTOKAN_DOCUMENTS_PREUVES_BASE,
} from "@/pages/documentsPreuves/documentsPreuvesNavConfig.js";
import { ADMIN_SHOTOKAN_BASE } from "@/pages/shotokanBlackDragon/shotokanNavConfig.js";
import { localizeDemoText } from "@/i18n/localizeDemoText.js";

function useLocale() {
  const { lang } = useI18n();
  return lang === "en" ? "en" : "fr";
}

function QuickActionLink({ to, label, testId }) {
  return (
    <Link to={to} className="gsd-quick-actions__btn" data-testid={testId}>
      {label}
    </Link>
  );
}

export function DocumentsPreuvesDashboard() {
  const locale = useLocale();
  const store = useDataStore();
  const location = useLocation();
  const base = resolveDocumentsPreuvesBase(location.pathname);
  const inShotokan = base === SHOTOKAN_DOCUMENTS_PREUVES_BASE;

  const stats = useMemo(() => {
    const docs = [...(store.authorityDocuments || []), ...(store.dojoDocuments || [])];
    const dossiers = store.evidenceDossiers || [];
    const journal = store.validationLog || [];
    const imports = store.documentImportQueue || [];

    const pendingImports = imports.filter(
      (i) => i.statut_import !== "confirme" && i.statut_import !== "annule",
    );
    const lastImport = [...imports].sort((a, b) => (b.date_ajout || "").localeCompare(a.date_ajout || ""))[0];

    return {
      totalDocs: docs.length,
      toProvide: docs.filter((d) => d.statut_validation === "a_fournir").length,
      inReview: docs.filter((d) => d.statut_validation === "en_revision" || d.statut_validation === "recu").length,
      accepted: docs.filter((d) => d.statut_validation === "accepte").length,
      incompleteDossiers: dossiers.filter(
        (d) => d.statut_dossier === "incomplet" || (d.score_completude ?? 100) < 100,
      ).length,
      pendingImports: pendingImports.length,
      receivedValidationRequired: docs.filter((d) => d.statut_validation === "recu_validation_requise").length,
      lastImport,
      lastLog: [...journal].sort((a, b) => (b.date || "").localeCompare(a.date || ""))[0],
    };
  }, [store]);

  const quickLinks = [
    { segment: "documents", labelFr: "Voir les documents", labelEn: "View documents", testId: "dp-quick-documents" },
    { segment: "dossiers", labelFr: "Voir les dossiers", labelEn: "View dossiers", testId: "dp-quick-dossiers" },
    { segment: "validation", labelFr: "Voir la validation", labelEn: "View validation", testId: "dp-quick-validation" },
    { segment: "journal", labelFr: "Voir le journal", labelEn: "View log", testId: "dp-quick-journal" },
    { segment: "import", labelFr: "Importer un document", labelEn: "Import a document", testId: "dp-quick-import" },
    { segment: "securite", labelFr: "Voir sécurité future", labelEn: "View future security", testId: "dp-quick-securite" },
  ];

  return (
    <div data-testid="page-admin-documents-preuves">
      <GsdStandaloneModuleDashboard
        title={{ labelFr: DOCUMENTS_PREUVES_MODULE_FR, labelEn: DOCUMENTS_PREUVES_MODULE_EN }}
        subtitle={{
          labelFr: "Documents, dossiers, validation et traçabilité",
          labelEn: "Documents, dossiers, validation and traceability",
        }}
        icon="📄"
        lang={locale}
        testId="dp-coquille-dashboard"
        summaryCards={[
          { id: "total", label: locale === "en" ? "Total documents" : "Documents totaux", value: stats.totalDocs, testId: "dp-stat-total" },
          { id: "provide", label: locale === "en" ? "To provide" : "Documents à fournir", value: stats.toProvide, testId: "dp-stat-provide" },
          { id: "review", label: locale === "en" ? "Under review" : "Documents en révision", value: stats.inReview, testId: "dp-stat-review" },
          { id: "accepted", label: locale === "en" ? "Accepted" : "Documents acceptés", value: stats.accepted, testId: "dp-stat-accepted" },
          { id: "incomplete", label: locale === "en" ? "Incomplete dossiers" : "Dossiers incomplets", value: stats.incompleteDossiers, testId: "dp-stat-incomplete" },
          { id: "pending-imports", label: locale === "en" ? "Pending imports" : "Imports en attente", value: stats.pendingImports, testId: "dp-stat-pending-imports" },
          { id: "received-validation", label: locale === "en" ? "Received — validation required" : "Reçu — validation requise", value: stats.receivedValidationRequired, testId: "dp-stat-received-validation" },
        ]}
        quickActions={quickLinks.map((link) => ({
          id: link.testId,
          label: locale === "en" ? link.labelEn : link.labelFr,
          to: `${base}/${link.segment}`,
        }))}
        renderQuickAction={(action) => (
          <QuickActionLink key={action.id} to={action.to} label={action.label} testId={action.id} />
        )}
        infoBlocks={
          <>
            <GsdReadableBlock testId="dp-prudence-message">
              <p data-testid="dp-sensitive-notice">
                {locale === "en"
                  ? "Evidence documents may contain sensitive information. This version is a local demo; the official version will require a secure backend."
                  : "Les documents de preuve peuvent contenir des informations sensibles. Cette version est en démonstration locale; la version officielle nécessitera un backend sécurisé."}
              </p>
            </GsdReadableBlock>
            <GsdReadableBlock testId="dp-import-block">
              <p>
                <Link to={`${base}/import`} className="gsd-quick-actions__btn" data-testid="dp-quick-import-block">
                  {locale === "en" ? "Import a document" : "Importer un document"}
                </Link>
              </p>
              {stats.lastImport ? (
                <p className="muted" data-testid="dp-last-import">
                  {locale === "en" ? "Last local import" : "Dernier import local"} :{" "}
                  <strong>{localizeDemoText(stats.lastImport.fichier_nom, locale)}</strong> — {stats.lastImport.date_ajout}
                </p>
              ) : (
                <p className="muted" data-testid="dp-last-import-empty">
                  {locale === "en" ? "No local import yet." : "Aucun import local pour l'instant."}
                </p>
              )}
            </GsdReadableBlock>
            <GsdReadableBlock testId="dp-shotokan-link-block">
              <p data-testid="dp-shotokan-central-notice">
                {inShotokan
                  ? locale === "en"
                    ? "Documents & Evidence is integrated in the Shotokan Black Dragon module."
                    : "Documents & Preuves est intégré dans le module Shotokan Black Dragon."
                  : locale === "en"
                    ? "Authority documents linked to Shotokan Black Dragon titles remain visible in the Shotokan Black Dragon module, but are also centralized here."
                    : "Les documents d'autorité liés aux titres Shotokan Black Dragon restent visibles dans le module Shotokan Black Dragon, mais sont aussi centralisés ici."}
              </p>
              <p>
                <Link to={ADMIN_SHOTOKAN_BASE} data-testid="dp-link-shotokan-module">
                  {locale === "en" ? "Shotokan Black Dragon module" : "Module Shotokan Black Dragon"}
                </Link>
              </p>
            </GsdReadableBlock>
            {stats.lastLog ? (
              <GsdReadableBlock testId="dp-last-log-block">
                <p data-testid="dp-stat-last-log" className="muted">
                  {locale === "en" ? "Last journal action" : "Dernières actions journal"} :{" "}
                  <strong>{stats.lastLog.action}</strong> — {stats.lastLog.date?.slice(0, 16)}
                </p>
              </GsdReadableBlock>
            ) : null}
          </>
        }
      />
    </div>
  );
}
